import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { MailCheck, RefreshCw } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

const EmailVerification = () => {
  const [resending, setResending] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get email from location state
  const email = location.state?.email || 'your email';
  
  const handleResendVerification = async () => {
    if (resending) return;
    
    setResending(true);
    try {
      // First, trigger Supabase's built-in resend
      const { data, error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth?tab=signin`
        }
      });
      
      if (error) {
        throw error;
      }
      
      // Also send our custom, better-looking email
      try {
        await supabase.functions.invoke('send-verification-email', {
          body: {
            email,
            firstName: '', // We don't have this info here
            lastName: '',
            verificationUrl: `${window.location.origin}/auth?tab=signin&verification=pending`
          }
        });
        console.log('Custom verification email sent');
      } catch (customEmailError) {
        console.error('Error sending custom verification email:', customEmailError);
        // Don't throw here, as we've already sent Supabase's default email
      }
      
      toast.success('Verification email sent', {
        description: 'Please check your inbox and spam folder.',
      });
    } catch (error: any) {
      console.error('Error resending verification email:', error);
      toast.error('Failed to resend verification email', {
        description: error.message || 'Please try again later.',
      });
    } finally {
      setResending(false);
    }
  };
  
  const handleBackToLogin = async () => {
    await supabase.auth.signOut();
    navigate('/auth');
  };
  
  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <div className="w-full max-w-md p-8">
        <Card className="border-none shadow-md">
          <CardHeader className="space-y-1 items-center text-center pb-2">
            <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-4">
              <MailCheck className="h-8 w-8 text-blue-500" />
            </div>
            <CardTitle className="text-2xl font-bold">Welcome to Hivetivity!</CardTitle>
            <CardDescription className="text-base">We're excited to have you onboard.</CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-4 text-center pt-4">
            <div className="bg-blue-50 rounded-lg p-4">
              <h3 className="font-medium text-lg mb-2">Check your inbox</h3>
              <p className="text-muted-foreground mb-2">We've sent a verification email to</p>
              <p className="font-medium text-blue-600 break-all">{email}</p>
              <p className="text-sm text-muted-foreground mt-3">
                Please check your inbox and spam folder for an email from <span className="font-medium">noreply@hivetivity.com</span> and click the verification link to activate your account.
              </p>
            </div>
            
            <div className="pt-2 bg-gray-50 rounded-lg p-4">
              <h4 className="font-medium mb-2">Didn't get the email?</h4>
              <p className="text-sm text-muted-foreground mb-3">
                Sometimes verification emails can be delayed or filtered into spam folders. Check your spam/junk folder or try these options:
              </p>
              <Button 
                variant="outline" 
                className="flex items-center gap-2 mt-2"
                onClick={handleResendVerification}
                disabled={resending}
              >
                <RefreshCw className={`h-4 w-4 ${resending ? 'animate-spin' : ''}`} />
                {resending ? 'Sending...' : 'Send again'}
              </Button>
            </div>
          </CardContent>
          
          <CardFooter className="flex justify-center pt-2">
            <Button variant="link" onClick={handleBackToLogin}>
              Back to login
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default EmailVerification;
